import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";
import { useSeo } from "@/hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL || "";
const MAX_CHIPS = 8;

const assetUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${API}${url.startsWith("/") ? url : `/${url}`}`;
};

const fmtDate = (iso, lang) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { day: "numeric", month: "long", year: "numeric" });
};

const CardImage = ({ post }) =>
  post.image ? (
    <img src={assetUrl(post.image)} alt={post.title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
  ) : (
    <div className="aspect-[16/10] w-full bg-gradient-to-br from-[#4361ee] to-[#3050d8] flex items-end p-6">
      <span className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-white/70">{post.categories[0] || "A4P"}</span>
    </div>
  );

export default function BlogList() {
  const { t, lang } = useLang();
  const b = t.blog;
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);
  const [cat, setCat] = useState(null);

  useSeo({ title: b.title, description: b.sub, lang });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/blog/posts?lang=${lang}`)
      .then((r) => {
        if (!r.ok) throw new Error("load failed");
        return r.json();
      })
      .then((data) => !cancelled && setPosts(data))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const chips = useMemo(() => {
    if (!posts) return [];
    const counts = {};
    posts.forEach((p) => p.categories.forEach((c) => (counts[c] = (counts[c] || 0) + 1)));
    return Object.entries(counts)
      .sort((a, b2) => b2[1] - a[1])
      .slice(0, MAX_CHIPS)
      .map(([name]) => name);
  }, [posts]);

  const visible = useMemo(() => (posts || []).filter((p) => !cat || p.categories.includes(cat)), [posts, cat]);

  return (
    <div data-testid="blog-list-page" className="min-h-screen bg-[#FAFAFD] text-[#353535]">
      <Navbar accent="#4361ee" />

      <section className="px-5 sm:px-8 pt-32 sm:pt-40 pb-12">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#4361ee] mb-4">{b.kicker}</div>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">{b.title}</h1>
            <p className="mt-6 text-base text-[#353535]/60 font-jakarta max-w-xl">{b.sub}</p>
          </Reveal>
          {chips.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              <button
                data-testid="blog-filter-all"
                onClick={() => setCat(null)}
                className={`rounded-full px-4 py-2 text-xs font-jakarta font-semibold transition-colors duration-300 ${!cat ? "bg-[#353535] text-white" : "bg-black/5 text-[#353535]/70 hover:bg-black/10"}`}
              >
                {b.all}
              </button>
              {chips.map((c, i) => (
                <button
                  key={c}
                  data-testid={`blog-filter-chip-${i}`}
                  onClick={() => setCat(c === cat ? null : c)}
                  className={`rounded-full px-4 py-2 text-xs font-jakarta font-semibold transition-colors duration-300 ${cat === c ? "bg-[#4361ee] text-white" : "bg-black/5 text-[#353535]/70 hover:bg-black/10"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24 sm:pb-32">
        <div className="mx-auto max-w-[1400px]">
          {error && <p data-testid="blog-error" className="text-sm font-jakarta text-[#ca3b42]">{b.loadError}</p>}
          {!posts && !error && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="animate-pulse overflow-hidden rounded-3xl border border-black/10 bg-white">
                  <div className="aspect-[16/10] bg-black/5" />
                  <div className="p-7"><div className="h-4 w-2/3 rounded bg-black/5" /><div className="mt-3 h-3 w-full rounded bg-black/5" /><div className="mt-2 h-3 w-4/5 rounded bg-black/5" /></div>
                </div>
              ))}
            </div>
          )}
          {posts && visible.length === 0 && <p data-testid="blog-empty" className="text-sm font-jakarta text-[#999999]">{b.empty}</p>}
          {posts && visible.length > 0 && (
            <FadeStagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" delay={0.05}>
              {visible.map((p) => (
                <FadeItem key={p.slug}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-black/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(67,97,238,0.10)]">
                    <div className="overflow-hidden"><CardImage post={p} /></div>
                    <div className="flex flex-1 flex-col p-7">
                      <div className="flex items-center gap-3 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">
                        <span className="text-[#4361ee]">{p.categories[0] || "Blog"}</span>
                        <span aria-hidden="true">·</span>
                        <span>{fmtDate(p.date, lang)}</span>
                      </div>
                      <h2 className="mt-3 font-cabinet text-xl font-bold leading-snug">{p.title}</h2>
                      <p className="mt-3 text-sm font-jakarta text-[#353535]/60 leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-sm font-jakarta font-bold text-[#4361ee] transition-all duration-300 group-hover:gap-3.5">
                        {b.readMore} <ArrowRight size={15} />
                      </span>
                    </div>
                  </Link>
                </FadeItem>
              ))}
            </FadeStagger>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
